// src/services/googleCalendar.js
// Google Calendar REST API service (read-only, primary calendar)

const GCAL_API_BASE = 'https://www.googleapis.com/calendar/v3';

/**
 * Fetch events from user's primary Google Calendar.
 * @param {string} accessToken - OAuth2 access token with calendar.readonly scope
 * @param {Date|string} timeMin - Start of range
 * @param {Date|string} timeMax - End of range
 * @returns {Array} Formatted events compatible with react-big-calendar
 */
export const fetchGoogleCalendarEvents = async (accessToken, timeMin, timeMax) => {
  let allItems = [];
  let pageToken = null;

  do {
    const params = new URLSearchParams({
      timeMin: timeMin instanceof Date ? timeMin.toISOString() : timeMin,
      timeMax: timeMax instanceof Date ? timeMax.toISOString() : timeMax,
      singleEvents: 'true',
      orderBy: 'startTime',
      maxResults: '2500',
    });
    if (pageToken) params.set('pageToken', pageToken);

    const response = await fetch(
      `${GCAL_API_BASE}/calendars/primary/events?${params}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = new Error(`Google Calendar API error: ${response.status}`);
      error.status = response.status;
      try {
        const body = await response.json();
        error.message = body.error?.message || error.message;
      } catch (_) {}
      throw error;
    }

    const data = await response.json();
    allItems.push(...(data.items || []));
    pageToken = data.nextPageToken || null;
  } while (pageToken);

  return allItems
    .filter(item => item.status !== 'cancelled')
    .map(formatGoogleEvent);
};

/**
 * Transform a Google Calendar event into react-big-calendar compatible format.
 */
const formatGoogleEvent = (gcalEvent) => {
  const isAllDay = !!gcalEvent.start?.date;

  let start, end;
  if (isAllDay) {
    start = new Date(gcalEvent.start.date + 'T00:00:00');
    end = new Date(gcalEvent.end.date + 'T00:00:00');
  } else {
    start = new Date(gcalEvent.start.dateTime);
    end = new Date(gcalEvent.end.dateTime);
  }

  return {
    id: `gcal_${gcalEvent.id}`,
    googleCalendarId: gcalEvent.id,
    title: gcalEvent.summary || '(Sin título)',
    start,
    end,
    allDay: isAllDay,
    type: 'google_calendar',
    source: 'google_calendar',
    color: '#4285f4',
    description: gcalEvent.description || '',
    location: gcalEvent.location || '',
    htmlLink: gcalEvent.htmlLink,
    isReadOnly: true,
  };
};

/**
 * Simplify Google events for AI prompt consumption (reduce tokens).
 */
export const simplifyGoogleEventsForAI = (googleEvents) => {
  return googleEvents.map(event => ({
    title: event.title,
    start: event.start.toISOString(),
    end: event.end.toISOString(),
    allDay: event.allDay || undefined,
    location: event.location || undefined,
  }));
};
